import WorkVersionChip from "work/work-agreements/versions/_views/work-version-chip";
import WorkVersion from "work/work-agreements/versions/work-version";
import ForumIcon from "@mui/icons-material/ForumOutlined";
import { styled } from "@mui/material/styles";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import Guid from "_shared/guids/guid";
import CompanyColumnCell from "_shared/teams/cells/company-column-cell";
import LeaderColumnCell from "_shared/teams/cells/leader-column-cell";
import MembersColumnCell from "_shared/teams/cells/members-column-cell";
import {
  activeTeamPerspectiveOf,
  PERSPECTIVE_PARAMETER,
  WORKING_FOR_ME_PERSPECTIVE,
  ON_PERSPECTIVE,
} from "_shared/teams/teams-list-chips";
import { PageTab } from "_shared/teams/teams-tabs";
import TeamsToolbar, { TeamsInternalActions } from "_shared/teams/teams-toolbar";
import { useTeamsListUrl, useTeamsPageTabs } from "_shared/teams/use-teams-list";
import type { ListChipCounts } from "_shared/_views/list-pages/list-chip-selection";
import { uniqBy } from "lodash";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import {
  createMRTColumnHelper,
  type MRT_Row,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
  type MRT_Updater,
} from "material-react-table";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import useSessionPermissionsLoadState from "permissions/session/use-session-permissions-load-state";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import RowActions from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import { searchTextFieldProps, topToolbarFitsThePage } from "_shared/_views/tables/table-toolbar";
import {
  ActiveTeamOrderDirection,
  ActiveTeamOrderField,
  useGetActiveTeamsQuery,
} from "work/work-agreements/teams/_api/teams-api-service";
import ActiveTeam from "work/work-agreements/teams/active-team/active-team";
import { ActiveTeamJSON } from "work/work-agreements/teams/active-team/_schemas/active-team-schema";
import { DateCell } from "_shared/_views/tables/date-cells";
import { formatDate } from "_shared/formatting/dates";
import { LIST_PAGE_SIZE } from "_shared/_views/tables/table-page-size";

const NameLine = styled("span")(() => ({
  alignItems: "center",
  display: "inline-flex",
  minWidth: 0,
}));

const columnHelper = createMRTColumnHelper<ActiveTeam>();

/** The Work column's id, and the column the server sorts by name on. */
export const WORK_COLUMN = "work";

/** The Hired column's id, and the column the server sorts by hire date on. */
export const HIRED_COLUMN = "hired";

/**
 * Where the columns go when the pane is short of room (#6711, #6718). The team's people fold onto the
 * Team column, the members first. Hired is already under the work's name, so it folds to nothing.
 */
const activeTeamColumnFolds = {
  hired: { into: WORK_COLUMN, priority: 0 },
  members: { into: "team", priority: 1 },
  leader: { into: "team", priority: 2 },
} as const;

/**
 * The server's field for each sortable column (#5441). Only Work and Hired sort, because those are the
 * only columns the store holds: the firm, leader and member names are looked up after the page is cut,
 * so no store can order by them.
 */
const orderFieldByColumnId: Readonly<Record<string, ActiveTeamOrderField>> = {
  [WORK_COLUMN]: "WorkName",
  [HIRED_COLUMN]: "HireDate",
};

/**
 * Alphabetical by the work's name, which is how the list reads when nobody has asked for anything
 * else. Never empty: the server orders every answer, so "unsorted" would be an arrow meaning nothing.
 */
const DEFAULT_SORTING: SortingState = [{ id: WORK_COLUMN, desc: false }];

/** The server's order for the table's sort state (#5441). */
export function activeTeamOrderFrom(sorting: SortingState): {
  orderBy: ActiveTeamOrderField;
  orderDir: ActiveTeamOrderDirection;
} {
  const first = sorting.length > 0 ? sorting[0] : undefined;
  return {
    orderBy: first?.id === HIRED_COLUMN ? "HireDate" : "WorkName",
    orderDir: first?.desc ? "Descending" : "Ascending",
  };
}

/** The table's own state, as the URL holds it (#6712): the search, the sort and the page. */
type ActiveTeamTableState = {
  globalFilter: string;
  sorting: SortingState;
  pagination: PaginationState;
};

export function readActiveTeamTableState(searchParams: URLSearchParams): ActiveTeamTableState {
  const orderBy = searchParams.get("orderBy")?.toLowerCase();
  const columnId = Object.entries(orderFieldByColumnId).find(([, field]) => field.toLowerCase() === orderBy)?.[0];
  const sorting = columnId
    ? [{ id: columnId, desc: searchParams.get("orderDir") === OrderDirection.Descending }]
    : DEFAULT_SORTING;
  const { pageIndex, pageSize } = PaginationParameters.fromListPageSearchParams(searchParams);
  return {
    globalFilter: searchParams.get("query") ?? "",
    sorting,
    pagination: { pageIndex, pageSize: searchParams.has("pageSize") ? pageSize : LIST_PAGE_SIZE },
  };
}

/** `current` with the table's state written over it. The chips are the page's, and stay as they are. */
export function writeActiveTeamTableState(state: ActiveTeamTableState, current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams();
  for (const perspective of current.getAll(PERSPECTIVE_PARAMETER)) next.append(PERSPECTIVE_PARAMETER, perspective);
  if (state.globalFilter) next.set("query", state.globalFilter);
  const order = activeTeamOrderFrom(state.sorting);
  next.set("orderBy", order.orderBy);
  next.set("orderDir", order.orderDir);
  next.set("pageIndex", String(state.pagination.pageIndex));
  next.set("pageSize", String(state.pagination.pageSize));
  return next;
}

function applyUpdater<TState>(updater: MRT_Updater<TState>, current: TState): TState {
  return typeof updater === "function" ? (updater as (old: TState) => TState)(current) : updater;
}

/** The page's teams, from one page of `work/active-teams`. */
function activeTeamsOn(page: Paginated<ActiveTeamJSON> | undefined): ActiveTeam[] | undefined {
  return page?.data.map((team) => ActiveTeam.fromJSON(team)).filter((team): team is ActiveTeam => Boolean(team));
}

/** How many people a team has, as it reads folded under Team: "3 members". */
export function foldedMembers(team: ActiveTeam): string | undefined {
  if (!(team instanceof ActiveTeam)) return undefined;
  const count = team.members.length;
  if (count < 1) return undefined;
  return count === 1 ? "1 member" : `${count} members`;
}

/** The leader's name as it reads folded under Team: "Led by Jane Doe". */
function FoldedLeader(props: Readonly<{ leaderUserId?: Guid }>) {
  const { leaderUserId } = props;
  const individuals = useSelector(getIndividualsByUserIds(leaderUserId ? [leaderUserId] : []));
  const leader = leaderUserId ? individuals.find((each) => each.userId.isEqualTo(leaderUserId)) : undefined;
  if (!leader) return null;
  return <>Led by {leader.fullName}</>;
}

function foldedLeader(team: ActiveTeam) {
  if (!(team instanceof ActiveTeam) || !team.leader) return undefined;
  return <FoldedLeader leaderUserId={team.leader.userId} />;
}

/** The work the team is on, as a link to the team, over when it was hired (#6718). */
function WorkCell({ row, table }: { row: MRT_Row<ActiveTeam>; table: MRT_TableInstance<ActiveTeam> }) {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  if (!(row.original instanceof ActiveTeam)) return null;
  const team = row.original;
  const hired = formatDate(team.hireDate);
  return (
    <CondensedCell
      title={
        <NameLine>
          <RowLink to={team.teamPath}>
            <SearchMatch table={table} text={team.workAgreementName} />
          </RowLink>
          <WorkVersionChip version={WorkVersion.hired(team.versionNumber)} />
        </NameLine>
      }
      secondary={hired ? `Hired ${hired}` : undefined}
    />
  );
}

/** The team's firm, over its side of the work: "Acme LLP", then "Vendor team" (#6718). */
function TeamCell({ row }: { row: MRT_Row<ActiveTeam> }) {
  if (!(row.original instanceof ActiveTeam)) return null;
  return <CondensedCell title={<CompanyColumnCell activeTeam={row.original} />} secondary={row.original.sideLabel} />;
}

// The search matches the work's name and the leader's name, so only those two columns mark it, and the
// rest never claim a match the server did not make (#6590).
const activeTeamColumns = [
  columnHelper.accessor((team) => team.workAgreementName, {
    header: "Work",
    id: WORK_COLUMN,
    meta: { kind: "name" },
    Cell: WorkCell,
    enableColumnFilter: false,
  }),
  columnHelper.accessor((team) => team.sideLabel, {
    header: "Team",
    id: "team",
    meta: { kind: "text" },
    Cell: TeamCell,
    enableGlobalFilter: false,
    enableSorting: false,
    enableColumnFilter: false,
  }),
  columnHelper.accessor((team) => team.leader?.userId.value ?? "", {
    header: "Leader",
    id: "leader",
    meta: { kind: "person", fold: { ...activeTeamColumnFolds.leader, render: foldedLeader } },
    Cell: ({ row, table }) => <LeaderColumnCell activeTeam={row.original} table={table} />,
    enableSorting: false,
    enableColumnFilter: false,
  }),
  columnHelper.accessor((team) => team.members.length, {
    header: "Members",
    id: "members",
    meta: { kind: "people", fold: { ...activeTeamColumnFolds.members, render: foldedMembers } },
    Cell: ({ row }) => (
      <MembersColumnCell
        activeTeam={row.original}
        teamName={WorkVersion.nameWithVersion(row.original.workAgreementName, WorkVersion.hired(row.original.versionNumber))}
      />
    ),
    enableGlobalFilter: false,
    enableSorting: false,
    enableColumnFilter: false,
  }),
  // Sortable in Show/hide, and already read under the work's name, so it starts hidden and folds to nothing.
  columnHelper.accessor((team) => formatDate(team.hireDate) ?? "", {
    header: "Hired",
    id: HIRED_COLUMN,
    meta: { kind: "date", fold: { ...activeTeamColumnFolds.hired, render: () => undefined } },
    Cell: ({ row }) => <DateCell value={row.original.hireDate} />,
    enableColumnFilter: false,
    enableGlobalFilter: false,
  }),
];

/**
 * The Teams tab (#6718): every team the reader is on and every vendor team working for them, on work
 * that is hired and running. The I'm on and Working for me chips narrow it; with neither, or both, the
 * list holds both, so a client team leader whose vendor team works for them sees one agreement as two
 * rows, one per team.
 */
export default function ActiveTeams() {
  const session = useSession();
  const navigate = useNavigate();
  const sessionPermissions = useSelector(getSessionPermissions);
  const permissionsLoadState = useSessionPermissionsLoadState();

  const { searchParams, searchKey, chips, showReaderChange, onTabChange, onChipsChange } = useTeamsListUrl(
    PageTab.Teams,
  );
  const tableState = useMemo(() => readActiveTeamTableState(new URLSearchParams(searchKey)), [searchKey]);
  const { globalFilter, sorting, pagination } = tableState;
  const perspective = activeTeamPerspectiveOf(chips);

  // The search, the order, the page and the perspective all go to the server (#5441, #6557). The hook's
  // cache is keyed by the page asked for, so returning to a page already fetched shows that page.
  const order = activeTeamOrderFrom(sorting);
  const activeTeamsQuery = useGetActiveTeamsQuery({
    perspective,
    search: globalFilter,
    orderBy: order.orderBy,
    orderDir: order.orderDir,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });
  // currentData, not data: data keeps the last answer while another loads or after it fails, so it
  // would show one chip's teams under another.
  const activeTeamsPage = activeTeamsQuery.currentData;
  const activeTeams = useMemo(() => activeTeamsOn(activeTeamsPage), [activeTeamsPage]);
  const isLoadingTeams = activeTeamsQuery.isLoading || activeTeamsQuery.isFetching;
  const errorLoadingTeams = activeTeamsQuery.isError;

  const { tabs, teamCounts } = useTeamsPageTabs({ activeTab: PageTab.Teams, search: globalFilter, session });
  const chipCounts = useMemo<ListChipCounts>(
    () => ({
      [PERSPECTIVE_PARAMETER]: { [ON_PERSPECTIVE]: teamCounts?.on, [WORKING_FOR_ME_PERSPECTIVE]: teamCounts?.workingForMe },
    }),
    [teamCounts?.on, teamCounts?.workingForMe],
  );

  const activeTeamUserIds = useMemo(() => getActiveTeamUserIds(activeTeams ?? []), [activeTeams]);
  useGetIndividualsByUserIdsQuery(
    { userIds: activeTeamUserIds.map((id) => id.value) },
    { skip: activeTeamUserIds.length === 0 },
  );

  const updateTableState = useCallback(
    (change: Partial<ActiveTeamTableState>) =>
      showReaderChange(writeActiveTeamTableState({ ...tableState, ...change }, searchParams)),
    [searchParams, showReaderChange, tableState],
  );

  const tableData = useMemo(() => activeTeams ?? [], [activeTeams]);
  const searchSubject = "teams";

  const tableOptions: MRT_TableOptions<ActiveTeam> = {
    columns: activeTeamColumns,
    data: tableData,
    enableRowSelection: false,
    enableTableHead: true,
    // A row opens its team, on its agreement's own team tab (#6718).
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        if (row.original instanceof ActiveTeam) void navigate(row.original.teamPath);
      },
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => <TeamsInternalActions table={table} />,
    renderTopToolbarCustomActions: ({ table }) => (
      <TeamsToolbar
        table={table}
        activeTab={PageTab.Teams}
        tabs={tabs}
        onTabChange={onTabChange}
        chips={chips}
        chipCounts={chipCounts}
        onChipsChange={onChipsChange}
      />
    ),
    // One team per side of an agreement, and an agreement can be a row on each side, so a row is keyed
    // by both. While the table loads, MRT hands this its placeholder rows, which are not teams, so they
    // are keyed by position.
    getRowId: (row, index) => (row instanceof ActiveTeam ? row.rowId : `loading-${index}`),
    // The ⋯ that ends every row: Open, to the team, then Message team, to its chat (#6718).
    enableRowActions: true,
    enableColumnPinning: true,
    renderRowActions: ({ row }) => {
      if (!(row.original instanceof ActiveTeam)) return null;
      const team = row.original;
      return (
        <RowActions
          open={{ to: team.teamPath }}
          ariaLabel={`Actions for ${team.workAgreementName}, ${team.sideLabel.toLowerCase()}`}
          actions={[
            {
              key: "message",
              label: "Message team",
              ariaLabel: `Message the ${team.sideLabel.toLowerCase()} on ${team.workAgreementName}`,
              icon: <ForumIcon />,
              inMenu: true,
              onClick: () => void navigate(team.conversationPath),
            },
          ]}
        />
      );
    },
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
      columnVisibility: { [HIRED_COLUMN]: false },
      showGlobalFilter: true,
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) => searchTextFieldProps({ searches: searchSubject, table }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: errorLoadingTeams,
      isLoading: isLoadingTeams,
      onRetry: () => void activeTeamsQuery.refetch(),
      subject: searchSubject,
    }),
    manualFiltering: true,
    enableColumnFilters: false,
    enableSortingRemoval: false,
    // The server orders by one column, so a second sort indicator would promise an order it never sends.
    enableMultiSort: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: { sx: { flex: 1, minWidth: 0 } },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
      },
    },
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiToolbarAlertBannerProps: errorLoadingTeams ? { color: "error", children: "Error loading data" } : undefined,
    // A new search or a new order is a new list, and a new list starts on its first page. Otherwise a
    // reader who narrows the search lands on page four of something whose page one they never saw.
    onGlobalFilterChange: (updater: MRT_Updater<string>) =>
      updateTableState({
        globalFilter: applyUpdater(updater, globalFilter) ?? "",
        pagination: { ...pagination, pageIndex: 0 },
      }),
    onPaginationChange: (updater: MRT_Updater<PaginationState>) => {
      const next = applyUpdater(updater, pagination);
      // A different page size is a different set of pages.
      updateTableState({ pagination: next.pageSize === pagination.pageSize ? next : { ...next, pageIndex: 0 } });
    },
    onSortingChange: (updater: MRT_Updater<SortingState>) => {
      const next = applyUpdater(updater, sorting);
      updateTableState({
        sorting: next.length > 0 ? next : DEFAULT_SORTING,
        pagination: { ...pagination, pageIndex: 0 },
      });
    },
    // Every team that matches, from X-Pagination, not the length of the page in hand.
    rowCount: activeTeamsPage?.headers.totalElements ?? 0,
    state: {
      globalFilter,
      isLoading: !activeTeams?.length && isLoadingTeams,
      pagination,
      showAlertBanner: errorLoadingTeams,
      showProgressBars: isLoadingTeams,
      sorting,
      showSkeletons: !activeTeams?.length && isLoadingTeams,
    },
  };

  if (!sessionPermissions) return permissionsLoadState;
  if (!sessionPermissions.includesAccessToTeams) return <NotAuthorized />;

  return (
    <MRTNoMemo<ActiveTeam> options={tableOptions} persistColumnVisibilityAs="teams-active" tableKey="teams" cards />
  );
}

function getActiveTeamUserIds(teams: ActiveTeam[]): Guid[] {
  if (teams.length === 0) return [];
  return uniqBy(
    teams.flatMap((team) => [
      ...(team.leader?.userId ? [team.leader.userId] : []),
      ...(Array.isArray(team.members) ? team.members.map((m) => m.userId) : []),
    ]),
    (id) => id.value,
  );
}
